/**
 * src/pages/[section]/[slug].tsx
 */
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import fs from 'fs';
import path from 'path';
import { generateFullPageMetadata, PageHeader, Body, SocialShareButtons, Related, Library, MediaCarousel } from './../../components';
import { relevantVideos, relevantPdfs } from '@/lib/media/carousels';
import { DocTools } from './../../components';
import { build_content } from './../../utils/content_parser';
import { getPagePaths, resolveThumbnailInfoJson } from './../../functions';
import { getAllPageDefinitions } from './../../utils/contentWalker';
import { derivePageDates } from './../../utils/page_dates';
import { extractYouTubeIds } from '@/lib/media/schema';
import { collectPageImages } from '@/lib/media/resolve';
import type { ImageData, PageData, SourceProps } from '@interfaces';

const titleCase = (s: string) =>
  s.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const DynamicPage: NextPage<SourceProps & { relVideos?: any[]; relPdfs?: any[] }> = ({ pageData, imageData, relVideos = [], relPdfs = [] }) => {
  const metadata = generateFullPageMetadata(pageData, imageData);
  return (
    <>
      <Head>
        <title>{metadata.pageTitle}</title>
        <link rel="canonical" href={metadata.canonicalUrl} />
        {Object.entries(metadata.meta).map(([name, content]) => (
          <meta key={name} name={name} content={content} />
        ))}
        {Object.entries(metadata.openGraph).map(([property, value]) => {
          if (Array.isArray(value)) {
            return value.map((v, i) => (
              <meta key={`${property}-${i}`} property={property} content={v} />
            ));
          }
          return <meta key={property} property={property} content={value} />;
        })}
        {Object.entries(metadata.twitter).map(([name, content]) => (
          <meta key={name} name={name} content={content} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(metadata.schemaGraph) }}
        />
      </Head>
      <SocialShareButtons
        text={pageData.description}
        url={pageData.share_url}
        via="thedailydialectics"
        hashtags={pageData.keywords_str}
        title={pageData.title}
        description={pageData.description}
        summary={pageData.summary}
        image={metadata.primaryImage}
      />
      <PageHeader meta={pageData} baseUrl={pageData.BASE_URL} />
      {pageData.library ? <Library data={pageData.library} /> : null}
      {(pageData as any).doc_tools ? <DocTools filename={`${pageData.title || 'document'}.txt`} label={pageData.title || 'Document'} /> : null}
      <Body meta={pageData} pdfViewer={pageData?.pdfViewer} />
      <MediaCarousel title="Related videos" items={relVideos} />
      <MediaCarousel title="Related documents" items={relPdfs} />
      <Related items={pageData.related} heading="Related content" />
    </>
  );
};

// ── Static paths ──────────────────────────────────────────────────────────────
export const getStaticPaths: GetStaticPaths = async () => {
  const pages = getAllPageDefinitions();
  return {
    paths: pages.map(({ section, slug }) => ({
      params: { section, slug },
    })),
    fallback: false,
  };
};

// ── Static props ──────────────────────────────────────────────────────────────
export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const dirname  = params?.section as string;
    const filename = params?.slug    as string;

    const { jsonPath, contentsPath } = getPagePaths(dirname, filename);
    const pageDir = path.dirname(contentsPath);

    const pageData: PageData = JSON.parse(fs.readFileSync(jsonPath, 'utf8'));

    // Resolve the hero thumbnail, but a missing/legacy thumbnail must NOT 404
    // the whole page — degrade to no hero. PageHeader renders the hero from
    // pageData.imageData (meta.imageData) when present.
    let imageData: ImageData = {} as ImageData;
    try {
      const thumbnailInfoPath = resolveThumbnailInfoJson(pageData.thumbnail, pageDir);
      imageData = JSON.parse(fs.readFileSync(thumbnailInfoPath, 'utf8'));
      pageData.imageData = imageData;
    } catch (e) {
      console.warn(`[${dirname}/${filename}] thumbnail unresolved: ${(e as Error).message}`);
    }

    // images.json (compiled from images.ts by compile_page_images.py) holds
    // macro strings, not paths — raw text is scanned for image ids below,
    // together with content.md, and resolved onto pageData.images.
    const imagesJsonPath = path.join(pageDir, 'images.json');
    const imagesJsonText = fs.existsSync(imagesJsonPath)
      ? fs.readFileSync(imagesJsonPath, 'utf8')
      : '';

    // Load related.json if present — produced by scripts/generate_related.mjs
    const relatedJsonPath = path.join(pageDir, 'related.json');
    if (fs.existsSync(relatedJsonPath)) {
      pageData.related = JSON.parse(fs.readFileSync(relatedJsonPath, 'utf8'));
    }

    // Load library.json if present — produced by scripts/build_education_page.mjs.
    // Generic hook: any page dir carrying a library.json gets the Reading Room
    // catalog treatment (see components/Library).
    const libraryJsonPath = path.join(pageDir, 'library.json');
    if (fs.existsSync(libraryJsonPath)) {
      pageData.library = JSON.parse(fs.readFileSync(libraryJsonPath, 'utf8'));
    }

    // Real dates: explicit variables.json field when present, else the mtimes
    // of variables.json/content.md. Threaded into article:*_time metas and the
    // Article JSON-LD by generateFullPageMetadata — never fabricated at build.
    const dates = derivePageDates(jsonPath, contentsPath);
    if (dates) {
      pageData.datePublished = dates.datePublished;
      pageData.dateModified = dates.dateModified;
    }

    // A handful of variables.json carry a relative/garbage share_url (e.g.
    // "/imgs/…"). We KNOW the true route here — normalize so the canonical,
    // og:url, share buttons and breadcrumbs all agree on the real page URL.
    const base = (pageData.BASE_URL || 'https://thedailydialectics.com').replace(/\/+$/, '');
    if (!/^https?:\/\//i.test(pageData.share_url || '')) {
      pageData.share_url = `${base}/${dirname}/${filename}`;
    }

    // Breadcrumb trail (Home → Section → Page) for BreadcrumbList JSON-LD,
    // titled from related_index.json — the same source the visible related/nav
    // cards are built from — falling back to this page's own title.
    let crumbTitle = (pageData.title || '').trim();
    try {
      const pagesRoot = path.dirname(path.dirname(pageDir));
      const idxPath = path.join(pagesRoot, 'related_index.json');
      if (fs.existsSync(idxPath)) {
        const idx = JSON.parse(fs.readFileSync(idxPath, 'utf8')) as Record<string, any>;
        const entry = idx[`${dirname}/${filename}`];
        if (entry?.title) crumbTitle = String(entry.title).trim();
      }
    } catch {
      /* breadcrumb titles degrade to pageData.title */
    }
    pageData.breadcrumbs = [
      { name: 'Home', item: `${base}/` },
      { name: titleCase(dirname), item: `${base}/${dirname}` },
      { name: crumbTitle, item: pageData.share_url || `${base}/${dirname}/${filename}` },
    ];

    const rawContent = fs.readFileSync(contentsPath, 'utf8');

    // Every image this page selects (images.json + content.md macros),
    // resolved to real files — og:image and Article JSON-LD list them all,
    // with the hero thumbnail deduped in generateFullPageMetadata.
    pageData.images = collectPageImages([imagesJsonText, rawContent]).map((m) => m.src);

    // Extract YouTube embeds before parsing so the page emits VideoObject
    // JSON-LD for every video it references (set on pageData.videos, consumed
    // by generateFullPageMetadata). Merge with any explicit list.
    const ytIds = extractYouTubeIds(rawContent);
    if (ytIds.length) {
      const existing = Array.isArray(pageData.videos) ? pageData.videos : [];
      pageData.videos = [...existing, ...ytIds];
    }

    pageData.content = await build_content(rawContent, pageData, filename);

    const relVideos = relevantVideos(pageData.keywords_str, dirname, 12, `${dirname}/${filename}`);
    const relPdfs = relevantPdfs(pageData.keywords_str, dirname, 12, `${dirname}/${filename}`);

    return { props: { pageData, imageData, relVideos, relPdfs } };
  } catch (error) {
    console.error('Dynamic page build error:', error);
    return { notFound: true };
  }
};

export default DynamicPage;